import { lazy, Suspense, useEffect, useRef, useMemo } from 'react'

import { Form } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import 'react-quill/dist/quill.snow.css'
import styled from 'styled-components'

import { KeyboardKey } from 'src/types'

import useDebounce from '../../hooks/use-debounce'

import { makeFieldNameWithPrefix } from './util'
import Wrapper, { FormFieldWrapperProps } from './Wrapper'

const ReactQuill = lazy(() => import('react-quill'))

interface Props extends FormFieldWrapperProps {
  placeholder?: string
  onBlur?: (newVal: string) => void | Promise<void>
}

export const StyledQuill = styled(ReactQuill)`
  width: 100%;
  .ql-container {
    height: auto;
  }
  .ql-editor ul {
    display: flex;
    flex-direction: column;
  }
  .ql-editor ol {
    display: flex;
    flex-direction: column;
  }
`
export const QuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }, { align: [] }],
    ['link'],
  ],
  // https://github.com/slab/quill/issues/2905#issuecomment-883724079
  clipboard: {
    matchVisual: false,
  },
}

export const RichTextInput = (props: Props) => {
  const { placeholder: _, onBlur, ...formFieldWrapperProps } = props
  const { fieldName, fieldNamePrefix } = formFieldWrapperProps

  const form = Form.useFormInstance()
  const fullyQualifiedFieldName: NamePath = useMemo(
    () => makeFieldNameWithPrefix(fieldName, fieldNamePrefix),
    [fieldName, fieldNamePrefix]
  )
  Form.useWatch(fullyQualifiedFieldName, form)
  const value = form.getFieldValue(fullyQualifiedFieldName)
  const debouncedValue = useDebounce(value)
  const isMounted = useRef(false)

  useEffect(() => {
    if (isMounted.current) {
      onBlur?.(debouncedValue)
    } else {
      isMounted.current = true
    }
  }, [debouncedValue])

  return (
    <Suspense>
      <Wrapper {...formFieldWrapperProps}>
        {({ onValueChange, value: fieldValue }) => (
          <StyledQuill
            value={fieldValue}
            onChange={onValueChange}
            theme="snow"
            modules={QuillModules}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === KeyboardKey.K && (e.metaKey || e.ctrlKey)) {
                e.stopPropagation()
              }
            }}
          />
        )}
      </Wrapper>
    </Suspense>
  )
}
